import { useQuery } from "@tanstack/react-query";
import { apiGet } from "./client";
import { useTicketSize, type TicketSize } from "../ticket-size";
import type { RiskGrade, SparkPoint } from "./state-of-play";

export interface DealException {
  dealId: string;
  office: string;
  industry: string;
  grade: RiskGrade;
  financed: number;
  yield: number;
  requiredYield: number;
  /** netRav - targetNetMargin: how far short of target the deal lands */
  shortfall: number;
  grossRav: number;
  netRav: number;
  flaggedAt: string;
  fundedYear: number | null;
  /** components of required yield, so the drawer can explain the "why" */
  ftp: number;
  expectedLoss: number;
  opex: number;
  externalCommission: number;
  internalCommissionRate: number;
  internalCommission: number;
  internalCommissionDollars: number;
  targetNetMargin: number;
  grossMargin: number;
}

export interface BelowFloorCount {
  current: number;
  previousPeriod: number;
}

export interface SegmentScore {
  office: string;
  industry: string;
  /** Raw exception-deal count for this (office, industry) segment, descending. */
  count: number;
}

export interface DealExceptionsSummary {
  /** Only the current page -- see `total`/`page`/`pageSize`. */
  deals: DealException[];
  /** Total deals below floor across every page (~29,600 on a typical
   *  snapshot) -- use this for the "Deals below floor" stat, not
   *  `deals.length`, which is just the current page's size. */
  total: number;
  page: number;
  pageSize: number;
  belowFloorCount: BelowFloorCount;
  /** Top 10 (office, industry) segments by exception count, descending. */
  segmentScores: SegmentScore[];
  /** The single worst-shortfall deal across every page, not just the current
   *  one -- computed server-side so this stat stays correct no matter which
   *  page is being viewed. */
  worstShortfall: DealException | null;
}

export interface SegmentException {
  segmentId: string;
  label: string;
  blendedRav: number;
  floor: number;
  monthsBelowFloor: number;
  trend: SparkPoint[];
  primaryDriver: string;
  /** vintages dragging the average down */
  vintageDrag: Array<{ vintage: string; contribution: number; note: string }>;
  ravByMonth: Array<{ month: string; blendedRav: number; floor: number }>;
}

export const DEAL_EXCEPTIONS_PAGE_SIZE = 50;

/** Mirrors segments.ts's realized/projected RAV basis toggle -- "active" is
 *  the live/open book (deal_realized_tf = FALSE), "closed" is matured
 *  contracts (deal_realized_tf = TRUE). */
export type DealStatus = "active" | "closed";

export const exceptionsKeys = {
  deals: (page: number, pageSize: number, status: DealStatus, ticket?: TicketSize) =>
    ["exceptions", "deals", page, pageSize, status, ticket ?? "all"] as const,
  segments: (ticket?: TicketSize) => ["exceptions", "segments", ticket ?? "all"] as const,
};

export function fetchDealExceptions(
  page: number,
  pageSize: number,
  status: DealStatus,
  ticket?: TicketSize,
) {
  return apiGet<DealExceptionsSummary>("/exceptions/deals", {
    page: String(page),
    pageSize: String(pageSize),
    status,
    ticket,
  });
}

export function fetchSegmentExceptions(ticket?: TicketSize) {
  return apiGet<SegmentException[]>("/exceptions/segments", { ticket });
}

export function useDealExceptions(
  page: number,
  pageSize: number = DEAL_EXCEPTIONS_PAGE_SIZE,
  status: DealStatus = "active",
) {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: exceptionsKeys.deals(page, pageSize, status, ticket),
    queryFn: () => fetchDealExceptions(page, pageSize, status, ticket),
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  });
}

export function useSegmentExceptions() {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: exceptionsKeys.segments(ticket),
    queryFn: () => fetchSegmentExceptions(ticket),
    staleTime: 60_000,
  });
}
